import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, User, Lock, Calendar, Mail, Phone, Shield, ArrowLeft, Check } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function SettingsPage() {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const navigate = useNavigate();

  const [accountInfo, setAccountInfo] = useState(null);
  const [phoneInput, setPhoneInput] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/account-info`).then(res => setAccountInfo(res.data)).catch(() => {});
  }, []);

  const handleSavePhone = async () => {
    if (!phoneInput.trim()) { toast.error("Telefon numarası boş olamaz"); return; }
    setSavingPhone(true);
    try {
      await axios.post(`${API}/auth/update-phone`, { phone: phoneInput.trim() });
      setAccountInfo(prev => ({ ...prev, phone: phoneInput.trim() }));
      toast.success("Telefon numarası kaydedildi");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kayıt başarısız");
    } finally { setSavingPhone(false); }
  };

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error("Lütfen her iki alanı da doldurun");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Şifreler eşleşmiyor");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Şifre en az 8 karakter olmalı");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/auth/change-password`, { newPassword });
      toast.success(res.data.message);
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Şifre değiştirme başarısız");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
    } catch { return "-"; }
  };

  const roleLabel = (role) => {
    if (role === "player") return "Oyuncu";
    if (role === "venue_owner") return "Saha Sahibi";
    return role || "-";
  };

  return (
    <div className={`max-w-2xl mx-auto p-4 space-y-6 ${isDark ? "text-white" : ""}`} data-testid="settings-page">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className={`p-2 rounded-lg transition-colors ${isDark ? "hover:bg-[#3a3d40]" : "hover:bg-gray-100"}`}
          data-testid="settings-back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-2xl font-bold">Ayarlar</h1>
      </div>

      {/* Hesap Bilgileri */}
      <Card className={isDark ? "bg-[#2a2d2f] border-[#3a3d40]" : ""}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <User className="h-5 w-5" style={{ color: "rgba(147,112,219,0.8)" }} />
            Hesap Bilgileri
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {accountInfo ? (
            <>
              <InfoRow icon={<User className="h-4 w-4" />} label="Kullanıcı Adı" value={accountInfo.name} isDark={isDark} />
              <InfoRow icon={<Mail className="h-4 w-4" />} label="E-posta" value={accountInfo.email} isDark={isDark} />
              
              {/* Telefon - boşsa eklenebilir */}
              {accountInfo.phone ? (
                <InfoRow icon={<Phone className="h-4 w-4" />} label="Telefon" value={accountInfo.phone} isDark={isDark} />
              ) : (
                <div className={`flex items-center justify-between py-2 border-b ${isDark ? "border-[#3a3d40]" : "border-gray-100"}`}>
                  <div className="flex items-center gap-2">
                    <span className={isDark ? "text-gray-400" : "text-gray-500"}><Phone className="h-4 w-4" /></span>
                    <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>Telefon</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="tel"
                      value={phoneInput}
                      onChange={e => setPhoneInput(e.target.value)}
                      placeholder="05XX XXX XX XX"
                      className={`w-40 h-8 text-sm ${isDark ? "bg-[#1f2224] border-[#3a3d40] text-white" : ""}`}
                      data-testid="phone-input"
                    />
                    <button
                      onClick={handleSavePhone}
                      disabled={savingPhone || !phoneInput.trim()}
                      className="p-1.5 rounded-md text-white disabled:opacity-40"
                      style={{ background: "rgba(147,112,219,0.7)" }}
                      data-testid="save-phone-btn"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              <InfoRow icon={<Shield className="h-4 w-4" />} label="Rol" value={roleLabel(accountInfo.role)} isDark={isDark} />
              <InfoRow icon={<Calendar className="h-4 w-4" />} label="Üyelik Tarihi" value={formatDate(accountInfo.createdAt)} isDark={isDark} />
            </>
          ) : (
            <p className={isDark ? "text-gray-400" : "text-gray-500"}>Yükleniyor...</p>
          )}
        </CardContent>
      </Card>

      {/* Şifre Yenile */}
      <Card className={isDark ? "bg-[#2a2d2f] border-[#3a3d40]" : ""}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Lock className="h-5 w-5" style={{ color: "rgba(147,112,219,0.8)" }} />
            Şifre Yenile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className={isDark ? "text-gray-300" : ""}>Yeni Şifre</Label>
            <div className="relative">
              <Input
                type={showNew ? "text" : "password"}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Yeni şifrenizi girin"
                className={isDark ? "bg-[#1f2224] border-[#3a3d40] text-white" : ""}
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                data-testid="toggle-new-password"
              >
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className={isDark ? "text-gray-300" : ""}>Yeni Şifre (Tekrar)</Label>
            <div className="relative">
              <Input
                type={showConfirm ? "text" : "password"}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Yeni şifrenizi tekrar girin"
                className={isDark ? "bg-[#1f2224] border-[#3a3d40] text-white" : ""}
                data-testid="confirm-password-input"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                data-testid="toggle-confirm-password"
              >
                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {newPassword && confirmPassword && newPassword !== confirmPassword && (
            <p className="text-sm text-red-500">Şifreler eşleşmiyor</p>
          )}

          <Button
            onClick={handleChangePassword}
            disabled={saving || !newPassword || !confirmPassword || newPassword !== confirmPassword}
            className="w-full text-white border-0 disabled:opacity-50"
            style={{ background: "rgba(147,112,219,0.65)" }}
            onMouseEnter={e => !e.currentTarget.disabled && (e.currentTarget.style.background = "rgba(147,112,219,0.85)")}
            onMouseLeave={e => !e.currentTarget.disabled && (e.currentTarget.style.background = "rgba(147,112,219,0.65)")}
            data-testid="save-password-btn"
          >
            {saving ? "Kaydediliyor..." : "Şifreyi Kaydet"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function InfoRow({ icon, label, value, isDark }) {
  return (
    <div className={`flex items-center justify-between py-2 border-b ${isDark ? "border-[#3a3d40]" : "border-gray-100"}`}>
      <div className="flex items-center gap-2">
        <span className={isDark ? "text-gray-400" : "text-gray-500"}>{icon}</span>
        <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>{label}</span>
      </div>
      <span className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>{value}</span>
    </div>
  );
}
